import React, { useState, useRef, useEffect } from "react";
import {
  Menu,
  X,
  Home,
  Info,
  // Settings,
  Camera,
  MessageCircle,
  Globe,
  Heart,
  Volume2,
  VolumeX,
} from "lucide-react";

const Navigation = ({
  currentPage,
  setCurrentPage,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const [isMantraPlaying, setIsMantraPlaying] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const audioRef = useRef(null);

  const navItems = [
    { id: "home", label: "Home", icon: Home },
    { id: "about", label: "About Us", icon: Info },
    // { id: "services", label: "Services", icon: Settings },
    { id: "gallery", label: "Gallery", icon: Camera },
    { id: "donation", label: "Donate", icon: Heart },
    { id: "contact", label: "Contact", icon: MessageCircle },
    { id: "online-services", label: "Online Services", icon: Globe },
    // { id: 'payments', label: 'Payments', icon: MessageCircle },
  ];

  const localOmAudioUrl = "/audio/temple-chant-loop.mp3";

  useEffect(() => {
    console.log("Initializing local temple audio...");
    setIsAudioReady(true);

    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.volume = 0.7;
    }

    return () => {
      console.log("Cleaning up local temple audio...");
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  const toggleMantra = async () => {
    console.log("Toggle mantra clicked. Current state:", isMantraPlaying);

    if (!isAudioReady) {
      alert("Audio system not ready. Please wait a moment and try again.");
      return;
    }

    try {
      const audio = audioRef.current || new Audio(localOmAudioUrl);
      audio.src = localOmAudioUrl;
      audio.loop = true;
      audio.volume = 0.7;
      audioRef.current = audio;

      if (isMantraPlaying) {
        console.log("Stopping mantra...");
        audio.pause();
        audio.currentTime = 0;
        setIsMantraPlaying(false);
      } else {
        console.log("Starting mantra from local temple audio...");
        await audio.play();
        setIsMantraPlaying(true);
      }
    } catch (error) {
      console.error("Error in toggleMantra:", error);
      alert("Audio error occurred. Please try again.");
    }
  };

  return (
    <nav className="bg-gradient-to-r from-yellow-500 to-red-600 shadow-lg sticky top-0 z-50">
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center min-w-0 flex-shrink mr-2">
            <div className="flex-shrink-0 flex items-center min-w-0">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden mr-1 sm:mr-2 transition-all duration-300 flex-shrink-0 border-2 border-yellow-300 shadow-sm ${
                  isMantraPlaying
                    ? "animate-pulse ring-4 ring-yellow-300 ring-opacity-50"
                    : "bg-yellow-100"
                }`}
              >
                <img
                  src="/images/Logo01.png"
                  alt="Temple logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-white font-bold text-xs sm:text-sm lg:text-base xl:text-lg truncate">
                Sri Kalikamundeshwari Temple Trust
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-3 xl:space-x-5">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentPage(item.id)}
                  className={`flex items-center space-x-1 px-2 xl:px-3 py-2 rounded-md text-xs xl:text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    currentPage === item.id
                      ? "bg-yellow-400 text-orange-800 shadow-lg transform scale-105"
                      : "text-white hover:bg-orange-500 hover:scale-105"
                  } ${item.id === "donation" ? "animate-pulse" : ""}`}
                >
                  <IconComponent size={16} />
                  <span className="hidden xl:inline">{item.label}</span>
                </button>
              );
            })}

            {/* Om Mantra Toggle Button */}
            <button
              onClick={toggleMantra}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-300 border-2 transform hover:scale-105 ${
                isMantraPlaying
                  ? "bg-yellow-400 text-orange-800 shadow-lg scale-105 animate-pulse border-yellow-200 ring-2 ring-yellow-300"
                  : "text-white hover:bg-orange-500 border-white/30 hover:border-yellow-400 hover:shadow-lg"
              }`}
              title={isMantraPlaying ? "Stop Om Mantra" : "Play Om Mantra"}
              aria-label={isMantraPlaying ? "Stop Om mantra" : "Play Om mantra"}
            >
              {isMantraPlaying ? (
                <Volume2 size={16} className="animate-pulse" />
              ) : (
                <VolumeX size={16} />
              )}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center space-x-2">
            {/* Mobile Om Mantra Toggle */}
            <button
              onClick={toggleMantra}
              className={`p-2 rounded-lg transition-all duration-300 border-2 ${
                isMantraPlaying
                  ? "bg-yellow-400 text-orange-800 animate-pulse border-yellow-200 ring-2 ring-yellow-300"
                  : "text-white hover:text-yellow-400 border-white/30"
              }`}
              title={isMantraPlaying ? "Stop Om Mantra" : "Play Om Mantra"}
            >
              {isMantraPlaying ? <Volume2 size={22} /> : <VolumeX size={22} />}
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white hover:text-yellow-400 transition-colors duration-300"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-orange-700 shadow-lg animate-slideDown">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentPage(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 w-full px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                    currentPage === item.id
                      ? "bg-yellow-400 text-orange-800"
                      : "text-white hover:bg-orange-600"
                  }`}
                >
                  <IconComponent size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}

            {/* Mobile Om Mantra Toggle in Menu */}
            <button
              onClick={() => {
                toggleMantra();
                setIsMobileMenuOpen(false);
              }}
              className={`flex items-center space-x-2 w-full px-3 py-2 rounded-lg text-sm font-bold transition-all duration-300 border-2 ${
                isMantraPlaying
                  ? "bg-yellow-400 text-orange-800 animate-pulse border-yellow-200"
                  : "text-white hover:bg-orange-600 border-white/30"
              }`}
            >
              {isMantraPlaying ? <Volume2 size={18} /> : <VolumeX size={18} />}
              <span>
                {isMantraPlaying ? "Stop Om Mantra 🕉" : "Play Om Mantra 🕉"}
              </span>
            </button>
          </div>
        </div>
      )}

      <audio ref={audioRef} src={localOmAudioUrl} loop preload="auto" />
    </nav>
  );
};

export default Navigation;
